/**
 * Compose a home dashboard config from job role assignments and featured roles.
 * Build Contract §1D — should see layer.
 */

import type {
  CustomWidgetDef,
  HomeDashboardConfig,
  HomeSearchConfig,
  HomeWidgetId,
  WidgetColumn,
} from '../types/prioritization/homeDashboard';
import { DEFAULT_HOME_SEARCH } from '../types/prioritization/homeDashboard';
import { resolveActiveJobSlugs } from './contributorNavProfiles';
import { getAdminShellDashboardProfile, getRoleDashboardProfile } from './roleDashboardProfiles';

export interface ComposeHomeDashboardInput {
  assignedRoleNames: string[];
  featuredJobRoleSlugs?: string[];
  /** When true and no job slugs, use full enterprise dashboard default */
  useAdminShellDefault?: boolean;
}

function dedupePreserveOrder<T>(items: T[]): T[] {
  const seen = new Set<T>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

function mergePartialRecords<T extends string>(
  layers: Array<Partial<Record<T, unknown>> | undefined>,
): Partial<Record<T, unknown>> {
  return layers.reduce<Partial<Record<T, unknown>>>((acc, layer) => ({ ...acc, ...(layer ?? {}) }), {});
}

function defaultColumnForWidget(id: HomeWidgetId): WidgetColumn {
  if (
    id === 'proposals_awaiting_review' ||
    id === 'ball_position' ||
    id === 'my_responsibilities' ||
    id === 'watchlist' ||
    id === 'saved_searches' ||
    id === 'getting_started'
  ) {
    return 'left';
  }
  if (
    id === 'active_cycles' ||
    id === 'active_tracks' ||
    id === 'upcoming_sessions' ||
    id === 'stage_progress' ||
    id === 'cycle_readiness'
  ) {
    return 'center';
  }
  return 'right';
}

function buildColumns(widgetIds: HomeWidgetId[], ...columnLayers: Array<Partial<Record<HomeWidgetId, WidgetColumn>> | undefined>) {
  const merged = mergePartialRecords<HomeWidgetId>(columnLayers) as Partial<Record<HomeWidgetId, WidgetColumn>>;
  const columns: Partial<Record<HomeWidgetId, WidgetColumn>> = {};
  for (const id of widgetIds) {
    columns[id] = merged[id] ?? defaultColumnForWidget(id);
  }
  return columns;
}

export function resolveComposeJobSlugs(input: ComposeHomeDashboardInput): string[] {
  return resolveActiveJobSlugs(input.assignedRoleNames, input.featuredJobRoleSlugs);
}

export function composeHomeDashboard(input: ComposeHomeDashboardInput): HomeDashboardConfig {
  const jobSlugs = resolveComposeJobSlugs(input);

  if (jobSlugs.length === 0) {
    if (input.useAdminShellDefault) {
      const admin = getAdminShellDashboardProfile();
      return {
        layout: admin.layout ?? 'three-column',
        widgetIds: [...admin.widgetIds],
        widgetColumns: admin.widgetColumns,
        widgetHideIfEmpty: admin.widgetHideIfEmpty,
        customWidgets: [],
        search: admin.search ?? { ...DEFAULT_HOME_SEARCH },
        updatedAt: new Date().toISOString(),
      };
    }
    const baseline = getRoleDashboardProfile('session-participant');
    return finalizeComposed([baseline], baseline.search);
  }

  const profiles = jobSlugs.map((slug) => getRoleDashboardProfile(slug));
  const primarySearch = profiles[0]?.search;
  return finalizeComposed(profiles, primarySearch);
}

function finalizeComposed(
  profiles: ReturnType<typeof getRoleDashboardProfile>[],
  primarySearch?: HomeSearchConfig,
): HomeDashboardConfig {
  const widgetIds = dedupePreserveOrder(profiles.flatMap((p) => p.widgetIds));
  const widgetColumns = buildColumns(widgetIds, ...profiles.map((p) => p.widgetColumns));
  const widgetHideIfEmpty = mergePartialRecords<HomeWidgetId>(
    profiles.map((p) => p.widgetHideIfEmpty),
  ) as Partial<Record<HomeWidgetId, boolean>>;

  const layout = profiles.some((p) => p.layout === 'three-column') ? 'three-column' : 'two-column';

  const customWidgets: CustomWidgetDef[] = dedupePreserveOrder(
    profiles.flatMap((p) => (p as RoleDashboardProfileWithCustom).customWidgets ?? []),
  );

  return {
    layout,
    widgetIds,
    widgetColumns,
    widgetHideIfEmpty,
    customWidgets,
    search: primarySearch ? { ...DEFAULT_HOME_SEARCH, ...primarySearch } : { ...DEFAULT_HOME_SEARCH },
    updatedAt: new Date().toISOString(),
  };
}

type RoleDashboardProfileWithCustom = ReturnType<typeof getRoleDashboardProfile> & {
  customWidgets?: CustomWidgetDef[];
};

/** Convenience for stores — reads featured slugs from user state shape */
export function composeHomeDashboardForUser(input: {
  assignedRoleNames: string[];
  featuredJobRoleSlugs: string[];
  useContributorShell: boolean;
}): HomeDashboardConfig {
  return composeHomeDashboard({
    assignedRoleNames: input.assignedRoleNames,
    featuredJobRoleSlugs: input.featuredJobRoleSlugs,
    useAdminShellDefault: !input.useContributorShell,
  });
}
